import React, {
  useCallback, useMemo, useEffect, useState, ReactElement,
} from 'react';
import Card from '../../../../components/card/card.component';
import Table from '../../../../components/table/table.component';
import TableBody from '../../../../components/table-body/table-body.component';
import TableRow from '../../../../components/table-row/table-row.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import Button from '../../../../components/button/button.component';
import ModalButtonBlock from '../../../../components/modal-button-block/modal-button-block.component';
import RequiredMark from '../../../../components/required-mark/required-mark.component';
import ModalLayout from '../../../../components/modal-layout/modal-layout.component';
import ModalDescription from '../../../../components/modal-description/modal-description.component';
import ErrorBlock from '../../../../components/error-block/error-block.component';
import FieldSelect from '../../../../components/field-select/field-select.component';
import FieldText from '../../../../components/field-text/field-text.component';
import FieldTextRich from '../../../../components/field-text-rich/field-text-rich.component';
import FieldCheckbox from '../../../../components/field-checkbox/field-checkbox.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import { FILTER_VALUE_EMPTY } from '../../../../constants/system/filter-values';
import CompanyDefaultImage from '../../../../static/images/company-default-image.png';

const SettingsAccountNotificationModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isOpen,
    errorMessage,
    accountPlainList,
    onChange,
    onSubmitForm,
    onCloseModal,
  } = props;

  const [textValue, setTextValue]: [string | null, Function] = useState('');
  const [subjectValue, setSubjectValue]: [string | null, Function] = useState('');
  const [accountIdValue, setAccountIdValue]: [number | null, Function] = useState(null);
  const [isImportantValue, setIsImportantValue]: [boolean | null, Function] = useState(false);

  const isSaveDisabled: boolean = !textValue && !accountIdValue && accountIdValue !== FILTER_VALUE_EMPTY;

  const menuItemAccountPlainList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'name',
    optionList: accountPlainList,
    defaultImage: CompanyDefaultImage,
  }), [accountPlainList]);

  const onCancelForm: Function = useCallback((): void => {
    setTextValue('');
    setSubjectValue('');
    setAccountIdValue(null);
    setIsImportantValue(false);
  }, []);

  const cancelChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    onCancelForm();

    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  const onTextChange: Function = useCallback((event: React.ChangeEvent, value: string | null): void => {
    setTextValue(value);

    if (onChange) {
      onChange();
    }
  }, []);

  const onSubjectChange: Function = useCallback((value: string | null): void => {
    setSubjectValue(value);

    if (onChange) {
      onChange();
    }
  }, []);

  const onAccountChange: Function = useCallback((value: number | null): void => {
    setAccountIdValue(value);

    if (onChange) {
      onChange();
    }
  }, []);

  const onIsImportantChange: Function = useCallback((value: boolean): void => {
    setIsImportantValue(value);

    if (onChange) {
      onChange();
    }
  }, []);

  const onSaveFormClick: MouseEventHandlerFunction = useCallback((): void => {
    if (!accountIdValue && accountIdValue !== FILTER_VALUE_EMPTY && !textValue) {
      return;
    }

    if (onSubmitForm) {
      onSubmitForm(accountIdValue, textValue, subjectValue, isImportantValue, (): void => {
        setTextValue('');
        setSubjectValue('');
        setAccountIdValue(null);
        setIsImportantValue(false);
      });
    }
  }, [accountIdValue, textValue, subjectValue, isImportantValue]);

  useEffect((): void => {
    setTextValue('');
    setSubjectValue('');
    setAccountIdValue(null);
    setIsImportantValue(false);
  }, []);

  return (
    <ModalLayout isOpen={isOpen} title="Send Notification" onCloseModal={cancelChangesAndCloseModal}>
      <Card isContainer withNoPadding>
        <ModalDescription>
          This action will send an in-app global notification on behalf of the platform
          for users of selected account or for all users, if account field is blank.
        </ModalDescription>
        <Table>
          <TableBody isPlain withTopBorder withNoBorders withTwoColumns withHeaderColumn>
            <TableRow>
              <TableCell>
                Account
              </TableCell>
              <TableCell>
                <FieldSelect
                  isPlain
                  isBordered
                  withBlankElement
                  isDisabled={accountPlainList && !accountPlainList.length}
                  blankElementLabel="All"
                  optionsTotal={accountPlainList && accountPlainList.length}
                  value={accountIdValue}
                  onChange={(event: EventSelectChange, value: string): void => {
                    onAccountChange(value);
                  }}
                >
                  {menuItemAccountPlainList}
                </FieldSelect>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                Subject
              </TableCell>
              <TableCell>
                <FieldText
                  isPlain
                  isBordered
                  value={subjectValue}
                  onChange={(event: React.ChangeEvent): void => {
                    if (event.target) {
                      onSubjectChange((event.target as HTMLInputElement).value);
                    }
                  }}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                Message
                <RequiredMark />
              </TableCell>
              <TableCell>
                <FieldTextRich
                  isPlain
                  value={textValue}
                  onFieldChange={onTextChange}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                Is Important
              </TableCell>
              <TableCell>
                <FieldCheckbox
                  isPlain
                  value={isImportantValue}
                  onChange={(event: React.ChangeEvent, value: boolean): void => {
                    onIsImportantChange(value);
                  }}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Card>
      <ModalButtonBlock>
        <Button onClick={cancelChangesAndCloseModal}>
          Cancel
        </Button>
        <Button isActive isDisabled={isSaveDisabled} onClick={onSaveFormClick}>
          Send
        </Button>
      </ModalButtonBlock>
      {
        !!errorMessage && (
          <ErrorBlock errorMessage={errorMessage} />
        )
      }
    </ModalLayout>
  );
});

export default SettingsAccountNotificationModal;
